// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import SmallHeadingAndText from "@insite/content-library/Components/SmallHeadingAndText";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { checkoutSharedStyles } from "@sc/App/SharedStyles/CheckoutSharedStyles";
import React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId)?.value;
    return {
        cart,
        billTo: getBillToState(state, cart?.billToId)?.value,
        homePageUrl: getHomePageUrl(state),
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OnePageCheckoutConfirmationShippingStyles {
    container?: GridContainerProps;
    title?: TypographyPresentationProps;
    shippingMethodWrapper?: InjectableCss;
    dateWrapper?: InjectableCss;
    shipOnComplete?: TypographyPresentationProps;
}

export const onePageCheckoutConfirmationShippingStyles: OnePageCheckoutConfirmationShippingStyles = {};

const styles = onePageCheckoutConfirmationShippingStyles;

const OnePageCheckoutConfirmationShipping = ({ cart, billTo }: Props) => {
    if (!cart) {
        return null;
    }
    if (cart.fulfillmentMethod === FulfillmentMethod.Ship) {
        return null;
    }

    return (
        <>
            <Typography {...styles.title}>{translate("Pick Up")}</Typography>

            <StyledWrapper {...styles.container}>
                {cart.fulfillmentMethod === FulfillmentMethod.PickUp && cart.requestedPickupDate && (
                    <StyledWrapper {...styles.dateWrapper}>
                        <SmallHeadingAndText
                            {...checkoutSharedStyles.headingAndTextStyles}
                            heading={translate("Requested Pick Up Date")}
                            text={getLocalizedDateTime({
                                dateTime: new Date(cart.requestedPickupDate),
                                options: {
                                    year: "2-digit",
                                    month: "2-digit",
                                    day: "2-digit",
                                },
                            })}
                        />
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OnePageCheckoutConfirmationShipping),
    definition: {
        displayName: "Shipping",
        group: "One Page Checkout Confirmation",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
    },
};

export default widgetModule;
